// import axios from 'axios';
// axios.defaults.xsrfCookieName = 'csrftoken';
// axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';

// import api from '@/api.js';
import * as Sentry from '@sentry/browser';

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {},
  defaults: {
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFTOKEN',
  },
});

import store from '@/admin/store.js';

api.interceptors.request.use((req) => {
  if (store.state.auth.isAuthenticated) {
    req.headers.common['Authorization'] = 'Token ' + store.state.auth.token;
  }

  // Sentry.setExtra('request', req);
  return req;
});

api.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  (error) => {
    if (error.response.status === 401) {
      store.state.auth.isAuthenticated = false;
      store.state.auth.token = null;
      localStorage.removeItem('token_a');
    }
    if (error.response.status === 400) {
      Sentry.setExtra('response', error);
    }
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error

    return Promise.reject(error);
  }
);

const actions = {
  postAuth: function (data) {
    return api.post('admin/auth/', data);
  },
  getAuth: function (token) {
    return api.get('admin/auth/', {
      headers: {
        Authorization: 'Token ' + token,
      },
    });
  },
  /* Colors */
  getColors: function () {
    return api.get(uris.colorsURI);
  },
  getColorsUnpaginated: function () {
    return api.get(uris.colorsUnpaginatedURI);
  },
  postColor: function (data) {
    return api.post(uris.colorsURI, data);
  },
  getColor: function (reference) {
    return api.get(uris.colorURI(reference));
  },
  patchColor: function (reference, data) {
    return api.patch(uris.colorURI(reference), data);
  },
  deleteColor: function (reference) {
    return api.delete(uris.colorURI(reference));
  },

  /* Fabrics */
  getFabrics: function () {
    return api.get(uris.fabricsURI);
  },
  getFabricsUnpaginated: function () {
    return api.get(uris.fabricsUnpaginatedURI);
  },
  postFabric: function (data) {
    return api.post(uris.fabricsURI, data);
  },
  getFabric: function (reference) {
    return api.get(uris.fabricURI(reference));
  },
  patchFabric: function (reference, data) {
    return api.patch(uris.fabricURI(reference), data);
  },
  deleteFabric: function (reference) {
    return api.delete(uris.fabricURI(reference));
  },

  /* Interlinings */
  getInterlinings: function () {
    return api.get(uris.interliningsURI);
  },
  getInterliningsUnpaginated: function () {
    return api.get(uris.interliningsUnpaginatedURI);
  },
  postInterlining: function (data) {
    return api.post(uris.interliningsURI, data);
  },
  getInterlining: function (reference) {
    return api.get(uris.interliningURI(reference));
  },
  patchInterlining: function (reference, data) {
    return api.patch(uris.interliningURI(reference), data);
  },
  deleteInterlining: function (reference) {
    return api.delete(uris.interliningURI(reference));
  },

  /* Embroidered Strips */
  getEmbroideredStrips: function () {
    return api.get(uris.embroideredStripsURI);
  },
  getEmbroideredStripsUnpaginated: function (hasPlane = false) {
    return api.get(
      uris.embroideredStripsUnpaginatedURI + (hasPlane ? '&has_plane=1' : '')
    );
  },
  postEmbroideredStrip: function (data) {
    return api.post(uris.embroideredStripsURI, data);
  },
  getEmbroideredStrip: function (reference) {
    return api.get(uris.embroideredStripURI(reference));
  },
  patchEmbroideredStrip: function (reference, data) {
    return api.patch(uris.embroideredStripURI(reference), data);
  },
  deleteEmbroideredStrip: function (reference) {
    return api.delete(uris.embroideredStripURI(reference));
  },

  /* Initials */
  getInitials: function () {
    return api.get(uris.initialsURI);
  },
  getInitialsUnpaginated: function () {
    return api.get(uris.initialsUnpaginatedURI);
  },
  postInitial: function (data) {
    return api.post(uris.initialsURI, data);
  },
  getInitial: function (reference) {
    return api.get(uris.initialURI(reference));
  },
  patchInitial: function (reference, data) {
    return api.patch(uris.initialURI(reference), data);
  },
  deleteInitial: function (reference) {
    return api.delete(uris.initialURI(reference));
  },

  /* Formulas */
  getFormulas: function () {
    return api.get(uris.formulasURI);
  },
  getFormulasUnpaginated: function () {
    return api.get(uris.formulasUnpaginatedURI);
  },
  postFormula: function (data) {
    return api.post(uris.formulasURI, data);
  },
  getFormula: function (reference) {
    return api.get(uris.formulaURI(reference));
  },
  patchFormula: function (reference, data) {
    return api.patch(uris.formulaURI(reference), data);
  },
  deleteFormula: function (reference) {
    return api.delete(uris.formulaURI(reference));
  },

  /* Typographies */
  getTypographies: function () {
    return api.get(uris.typographiesURI);
  },
  getTypographiesUnpaginated: function () {
    return api.get(uris.typographiesUnpaginatedURI);
  },
  postTypography: function (data) {
    return api.post(uris.typographiesURI, data);
  },
  getTypography: function (reference) {
    return api.get(uris.formulaURI(reference));
  },
  patchTypography: function (reference, data) {
    return api.patch(uris.formulaURI(reference), data);
  },
  deleteTypography: function (reference) {
    return api.delete(uris.formulaURI(reference));
  },

  /* Embroidery Settings */
  getEmbroiderySettings: function () {
    return api.get(uris.embroiderySettingsURI);
  },
  getEmbroiderySettingsUnpaginated: function () {
    return api.get(uris.embroiderySettingsUnpaginatedURI);
  },
  postEmbroiderySetting: function (data) {
    return api.post(uris.embroiderySettingsURI, data);
  },
  getEmbroiderySetting: function (reference) {
    return api.get(uris.embroiderySettingURI(reference));
  },
  patchEmbroiderySetting: function (reference, data) {
    return api.patch(uris.embroiderySettingURI(reference), data);
  },
  deleteEmbroiderySetting: function (reference) {
    return api.delete(uris.embroiderySettingURI(reference));
  },

  /* Standard Embroideries */
  getStandardEmbroideries: function () {
    return api.get(uris.standardEmbroideriesURI);
  },
  getStandardEmbroideriesUnpaginated: function () {
    return api.get(uris.standardEmbroideriesUnpaginatedURI);
  },
  postStandardEmbroidery: function (data) {
    return api.post(uris.standardEmbroideriesURI, data);
  },
  getStandardEmbroidery: function (reference) {
    return api.get(uris.standardEmbroideryURI(reference));
  },
  patchStandardEmbroidery: function (reference, data) {
    return api.patch(uris.standardEmbroideryURI(reference), data);
  },
  deleteStandardEmbroidery: function (reference) {
    return api.delete(uris.standardEmbroideryURI(reference));
  },

  /* Customized Embroideries */
  getCustomizedEmbroideries: function () {
    return api.get(uris.customizedEmbroideriesURI);
  },
  getCustomizedEmbroideriesUnpaginated: function () {
    return api.get(uris.customizedEmbroideriesUnpaginatedURI);
  },
  postCustomizedEmbroidery: function (data) {
    return api.post(uris.customizedEmbroideriesURI, data);
  },
  getCustomizedEmbroidery: function (reference) {
    return api.get(uris.customizedEmbroideryURI(reference));
  },
  patchCustomizedEmbroidery: function (reference, data) {
    return api.patch(uris.customizedEmbroideryURI(reference), data);
  },
  deleteCustomizedEmbroidery: function (reference) {
    return api.delete(uris.customizedEmbroideryURI(reference));
  },

  /* Stitches */
  getStitches: function () {
    return api.get(uris.stitchesURI);
  },
  getStitchesUnpaginated: function () {
    return api.get(uris.stitchesUnpaginatedURI);
  },
  postStitch: function (data) {
    return api.post(uris.stitchesURI, data);
  },
  getStitch: function (reference) {
    return api.get(uris.stitchURI(reference));
  },
  patchStitch: function (reference, data) {
    return api.patch(uris.stitchURI(reference), data);
  },
  deleteStitch: function (reference) {
    return api.delete(uris.stitchURI(reference));
  },

  /* Product attributes */
  getProductAttributes: function () {
    return api.get(uris.productAttributesURI);
  },
  getProductAttributesUnpaginated: function () {
    return api.get(uris.productAttributesUnpaginatedURI);
  },
  postProductAttribute: function (data) {
    return api.post(uris.productAttributesURI, data);
  },
  getProductAttribute: function (reference) {
    return api.get(uris.productAttributeURI(reference));
  },
  patchProductAttribute: function (reference, data) {
    return api.patch(uris.productAttributeURI(reference), data);
  },
  deleteProductAttribute: function (reference) {
    return api.delete(uris.productAttributeURI(reference));
  },

  /* Product selectors */
  getProductSelectors: function () {
    return api.get(uris.productSelectorsURI);
  },
  getProductSelectorsByAttribute: function (attribute_id) {
    return api.get(uris.productSelectorsByAttributeURI(attribute_id));
  },
  getProductSelectorsByAttributeUnpaginated: function (attribute_id) {
    return api.get(
      uris.productSelectorsByAttributeUnpaginatedURI(attribute_id)
    );
  },

  getProductSelectorsUnpaginated: function () {
    return api.get(uris.productSelectorsUnpaginatedURI);
  },
  postProductSelector: function (data) {
    return api.post(uris.productSelectorsURI, data);
  },
  getProductSelector: function (reference) {
    return api.get(uris.productSelectorURI(reference));
  },
  patchProductSelector: function (reference, data) {
    return api.patch(uris.productSelectorURI(reference), data);
  },
  deleteProductSelector: function (reference) {
    return api.delete(uris.productSelectorURI(reference));
  },

  /* Product templates */
  getProductTemplates: function () {
    return api.get(uris.productTemplatesURI);
  },
  getProductTemplatesUnpaginated: function () {
    return api.get(uris.productTemplatesUnpaginatedURI);
  },
  postProductTemplate: function (data) {
    return api.post(uris.productTemplatesURI, data);
  },
  getProductTemplate: function (reference) {
    return api.get(uris.productTemplateURI(reference));
  },
  patchProductTemplate: function (reference, data) {
    return api.patch(uris.productTemplateURI(reference), data);
  },
  deleteProductTemplate: function (reference) {
    return api.delete(uris.productTemplateURI(reference));
  },

  /* Products */
  getProducts: function () {
    return api.get(uris.productsURI);
  },
  getProductsUnpaginated: function () {
    return api.get(uris.productsUnpaginatedURI);
  },
  getProductsWithStock: function (locationId) {
    return api.get(uris.productsWithStockURI(locationId));
  },
  getProductsWithStockGlobalSearch: function (locationId, txt, txtVariant) {
    return api.get(
      uris.productsWithStockGlobalSearchURI(locationId, txt, txtVariant)
    );
  },
  getProductsGlobalSearch: function (txt) {
    return api.get(uris.productsGlobalSearchURI(txt));
  },
  postProduct: function (data) {
    return api.post(uris.productsURI, data);
  },
  getProduct: function (reference) {
    return api.get(uris.productURI(reference));
  },
  patchProduct: function (reference, data) {
    return api.patch(uris.productURI(reference), data);
  },
  deleteProduct: function (reference) {
    return api.delete(uris.productURI(reference));
  },
  postProductQuotePrice: function (reference, data) {
    return api.post(uris.productQuotePriceURI(reference), data);
  },
  postProductDuplicate: function (id) {
    return api.post(uris.productDuplicateURI(id));
  },

  /* Product categories */
  getProductCategories: function () {
    return api.get(uris.productCategoriesURI);
  },
  getProductCategoriesUnpaginated: function () {
    return api.get(uris.productCategoriesUnpaginatedURI);
  },
  postProductCategory: function (data) {
    return api.post(uris.productCategoriesURI, data);
  },
  getProductCategory: function (reference) {
    return api.get(uris.productCategoryURI(reference));
  },
  patchProductCategory: function (reference, data) {
    return api.patch(uris.productCategoryURI(reference), data);
  },
  deleteProductCategory: function (reference) {
    return api.delete(uris.productCategoryURI(reference));
  },

  /* Embroidery categories */
  getEmbroideryCategories: function () {
    return api.get(uris.embroideryCategoriesURI);
  },
  getEmbroideryCategoriesUnpaginated: function () {
    return api.get(uris.embroideryCategoriesUnpaginatedURI);
  },
  postEmbroideryCategory: function (data) {
    return api.post(uris.embroideryCategoriesURI, data);
  },
  getEmbroideryCategory: function (reference) {
    return api.get(uris.embroideryCategoryURI(reference));
  },
  patchEmbroideryCategory: function (reference, data) {
    return api.patch(uris.embroideryCategoryURI(reference), data);
  },
  deleteEmbroideryCategory: function (reference) {
    return api.delete(uris.embroideryCategoryURI(reference));
  },

  /* Predefined product categories */
  getPredefinedProductCategories: function () {
    return api.get(uris.predefinedProductCategoriesURI);
  },
  getPredefinedProductCategoriesUnpaginated: function () {
    return api.get(uris.predefinedProductCategoriesUnpaginatedURI);
  },
  postPredefinedProductCategory: function (data) {
    return api.post(uris.predefinedProductCategoriesURI, data);
  },
  getPredefinedProductCategory: function (reference) {
    return api.get(uris.predefinedProductCategoryURI(reference));
  },
  patchPredefinedProductCategory: function (reference, data) {
    return api.patch(uris.predefinedProductCategoryURI(reference), data);
  },
  deletePredefinedProductCategory: function (reference) {
    return api.delete(uris.predefinedProductCategoryURI(reference));
  },

  /* Customers */
  getCustomers: function () {
    return api.get(uris.customersURI);
  },
  getCustomersUnpaginated: function () {
    return api.get(uris.customersUnpaginatedURI);
  },
  getCustomersGlobalSearch: function (txt) {
    return api.get(uris.customersGlobalSearchURI(txt));
  },
  postCustomer: function (data) {
    return api.post(uris.customersURI, data);
  },
  getCustomer: function (reference) {
    return api.get(uris.customerURI(reference));
  },
  patchCustomer: function (reference, data) {
    return api.patch(uris.customerURI(reference), data);
  },
  deleteCustomer: function (reference) {
    return api.delete(uris.customerURI(reference));
  },

  /* Billing Addresses */
  getBillingAddresses: function () {
    return api.get(uris.billingAddressesURI);
  },
  getBillingAddressesByCustomer: function (customerID, excludeHidden = 1) {
    return api.get(
      uris.billingAddressesByCustomerURI(customerID, excludeHidden)
    );
  },
  getBillingAddressesUnpaginated: function () {
    return api.get(uris.billingAddressesUnpaginatedURI);
  },
  postBillingAddress: function (data) {
    return api.post(uris.billingAddressesURI, data);
  },
  getBillingAddress: function (id) {
    return api.get(uris.billingAddressURI(id));
  },
  patchBillingAddress: function (id, data) {
    return api.patch(uris.billingAddressURI(id), data);
  },
  deleteBillingAddress: function (id) {
    return api.delete(uris.billingAddressURI(id));
  },

  /* Shipping Addresses */
  getPredefinedProductCategories: function () {
    return api.get(uris.shippingAddressesURI);
  },
  getShippingAddressesByCustomer: function (customerID, excludeHidden = 1) {
    return api.get(
      uris.shippingAddressesByCustomerURI(customerID, excludeHidden)
    );
  },
  postShippingAddress: function (data) {
    return api.post(uris.shippingAddressesURI, data);
  },
  getShippingAddress: function (reference) {
    return api.get(uris.shippingAddressURI(reference));
  },
  patchShippingAddress: function (reference, data) {
    return api.patch(uris.shippingAddressURI(reference), data);
  },
  deleteShippingAddress: function (reference) {
    return api.delete(uris.shippingAddressURI(reference));
  },

  /* Orders */

  getOrders: function () {
    return api.get(uris.ordersURI);
  },
  getOrdersUnpaginated: function () {
    return api.get(uris.ordersUnpaginatedURI);
  },
  postOrder: function (data) {
    return api.post(uris.ordersURI, data);
  },
  postOrderPrice: function (data) {
    return api.post(uris.orderPriceURI, data);
  },
  getOrder: function (reference) {
    return api.get(uris.orderURI(reference));
  },
  patchOrder: function (reference, data) {
    return api.patch(uris.orderURI(reference), data);
  },
  deleteOrder: function (reference) {
    return api.delete(uris.orderURI(reference));
  },
  downloadOrder: function (id) {
    return api.get(uris.orderDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },

  downloadOrderLegal: function (id) {
    return api.get(uris.orderLegalDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  generateInvoicePartial: function (id, data) {
    return api.post(uris.orderLegalDownloadPartialURI(id), data);
  },
  generateInvoiceTotal: function (id) {
    return api.post(uris.orderLegalDownloadTotalURI(id));
  },
  downloadOrderWorkshop: function (id) {
    return api.get(uris.orderWorkshopDownloadURI(id));
  },

  // Invoices
  downloadInvoice: function (id) {
    return api.get(uris.invoiceDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },

  deleteInvoice: function (id) {
    return api.delete(uris.invoiceURI(id));
  },

  /* Workshop Orders */
  getWorkshopOrders: function () {
    return api.get(uris.workshopOrdersURI);
  },
  getWorkshopOrdersUnpaginated: function () {
    return api.get(uris.workshopOrdersUnpaginatedURI);
  },
  postWorkshopOrder: function (data) {
    return api.post(uris.workshopOrdersURI, data);
  },
  getWorkshopOrder: function (reference) {
    return api.get(uris.workshopOrderURI(reference));
  },
  patchWorkshopOrder: function (reference, data) {
    return api.patch(uris.workshopOrderURI(reference), data);
  },
  deleteWorkshopOrder: function (reference) {
    return api.delete(uris.workshopOrderURI(reference));
  },
  downloadWorkshopOrder: function (id) {
    return api.get(uris.workshopOrderDownloadURI(id));
  },
  downloadWorkshopOrderLegal: function (id) {
    return api.get(uris.workshopOrderLegalDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  downloadWorkshopOrderWorkshop: function (id) {
    return api.get(uris.workshopOrderWorkshopDownloadURI(id));
  },
  postWorkshopOrderGetStock: function (data) {
    return api.post(uris.workshopOrderGetStockURI, data);
  },
  postWorkshopOrderGenerateInvoicePartial: function (id, data) {
    return api.post(uris.workshopOrderLegalDownloadPartialURI(id), data);
  },
  postWorkshopOrderGenerateInvoiceTotal: function (id, data) {
    return api.post(uris.workshopOrderLegalDownloadTotalURI(id), data);
  },
  postWorkshopOrderPreviewInvoiceTotal: function (id, data) {
    return api.post(uris.workshopOrderLegalPreviewTotalURI(id), data, {
      responseType: 'arraybuffer',
    });
  },
  postWorkshopOrderPreviewInvoicePartial: function (id, data) {
    return api.post(uris.workshopOrderLegalPreviewPartialURI(id), data, {
      responseType: 'arraybuffer',
    });
  },

  postWorkshopOrderSyncFromQuote: function (quoteId, data) {
    return api.post(uris.workshopOrderSyncFromQuoteURI(quoteId), data);
  },

  /* Deliveries */
  getDeliveries: function () {
    return api.get(uris.deliveriesURI);
  },
  getDeliveriesUnpaginated: function () {
    return api.get(uris.deliveriesUnpaginatedURI);
  },
  postDelivery: function (data) {
    return api.post(uris.deliveriesURI, data);
  },
  getDelivery: function (reference) {
    return api.get(uris.deliveryURI(reference));
  },
  patchDelivery: function (reference, data) {
    return api.patch(uris.deliveryURI(reference), data);
  },
  deleteDelivery: function (reference) {
    return api.delete(uris.deliveryURI(reference));
  },
  downloadDelivery: function (id) {
    return api.get(uris.deliveryDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  downloadDeliveryLegal: function (id) {
    return api.get(uris.deliveryLegalDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  downloaDelivery: function (id) {
    return api.get(uris.deliveryDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  postDeliveryGetStock: function (data) {
    return api.post(uris.workshopOrderGetStockURI, data);
  },

  /* Order status history */
  getOrdersUncompleted: function () {
    return api.get(uris.ordersUncompletedURI);
  },

  /* Workshop Order status history */
  getWorkshopOrderStatusHistoriesByWorkshopOrder: function (orderID) {
    return api.get(
      uris.workshopOrderStatusHistoriesByWorkshopOrderURI(orderID)
    );
  },
  postWorkshopOrderStatusHistory: function (data) {
    return api.post(uris.orderStatusHistoriesURI, data);
  },
  getWorkshopOrdersUncompleted: function () {
    return api.get(uris.ordersUncompletedURI);
  },

  /* Delivery status history */
  getDeliveryStatusHistoriesByDelivery: function (orderID) {
    return api.get(uris.deliveryStatusHistoriesByDeliveryURI(orderID));
  },
  postDeliveryStatusHistory: function (data) {
    return api.post(uris.orderStatusHistoriesURI, data);
  },
  getDeliveriesUncompleted: function () {
    return api.get(uris.ordersUncompletedURI);
  },

  /* Quotes */
  getQuotes: function () {
    return api.get(uris.quotesURI);
  },
  getQuotesUnpaginated: function () {
    return api.get(uris.quotesUnpaginatedURI);
  },
  getQuotesGlobalSearch: function (txt) {
    return api.get(uris.quotesGlobalSearchURI(txt));
  },
  postQuote: function (data) {
    return api.post(uris.quotesURI, data);
  },
  getQuote: function (reference) {
    return api.get(uris.quoteURI(reference));
  },
  patchQuote: function (reference, data) {
    return api.patch(uris.quoteURI(reference), data);
  },
  deleteQuote: function (reference) {
    return api.delete(uris.quoteURI(reference));
  },
  downloadQuote: function (id) {
    return api.get(uris.quoteDownloadURI(id), {
      responseType: 'arraybuffer',
    });
  },
  postQuotePrice: function (data) {
    return api.post(uris.quotePriceURI, data);
  },
  postQuoteMarkPaid: function (id) {
    return api.post(uris.quoteMarkPaidURI(id));
  },
  postQuoteDuplicate: function (id) {
    return api.post(uris.quoteDuplicateURI(id));
  },
  downloadQuoteWorkshop: function (id) {
    return api.get(uris.quoteWorkshopDownloadURI(id));
  },

  /* Countries */
  getCountries: function () {
    return api.get(uris.countriesURI);
  },
  getCountriesUnpaginated: function () {
    return api.get(uris.countriesUnpaginatedURI);
  },
  postCountry: function (data) {
    return api.post(uris.countriesURI, data);
  },
  getCountry: function (reference) {
    return api.get(uris.orderURI(reference));
  },
  patchCountry: function (reference, data) {
    return api.patch(uris.orderURI(reference), data);
  },
  deleteCountry: function (reference) {
    return api.delete(uris.orderURI(reference));
  },

  /* Shipping methods */
  getShippingMethods: function () {
    return api.get(uris.shippingMethodsURI);
  },
  getShippingMethodsUnpaginated: function () {
    return api.get(uris.shippingMethodsUnpaginatedURI);
  },
  postShippingMethod: function (data) {
    return api.post(uris.shippingMethodsURI, data);
  },
  getShippingMethod: function (reference) {
    return api.get(uris.shippingMethodURI(reference));
  },
  patchShippingMethod: function (reference, data) {
    return api.patch(uris.shippingMethodURI(reference), data);
  },
  deleteShippingMethod: function (reference) {
    return api.delete(uris.shippingMethodURI(reference));
  },

  /* fabriccompositions */
  getFabricCompositions: function () {
    return api.get(uris.fabricCompositionsUnpaginatedURI);
  },

  /* fabriccolorfamilies */
  getFabricColorFamilies: function () {
    return api.get(uris.fabricColorFamiliesUnpaginatedURI);
  },

  /* fabricdesigns */
  getFabricDesigns: function () {
    return api.get(uris.fabricDesignsUnpaginatedURI);
  },

  /* Products */
  getPredefinedProducts: function () {
    return api.get(uris.predefinedProductsURI);
  },
  getPredefinedProductsUnpaginated: function () {
    return api.get(uris.predefinedProductsUnpaginatedURI);
  },
  getPredefinedProductsWithStock: function (locationId) {
    return api.get(uris.predefinedProductsWithStockURI(locationId));
  },
  getPredefinedProductsWithStockGlobalSearch: function (locationId, txt) {
    return api.get(
      uris.predefinedProductsWithStockGlobalSearchURI(locationId, txt)
    );
  },
  getPredefinedProductsGlobalSearch: function (txt) {
    return api.get(uris.predefinedProductsGlobalSearchURI(txt));
  },
  postPredefinedProduct: function (data) {
    return api.post(uris.predefinedProductsURI, data);
  },
  getPredefinedProduct: function (reference) {
    return api.get(uris.predefinedProductURI(reference));
  },
  patchPredefinedProduct: function (reference, data) {
    return api.patch(uris.predefinedProductURI(reference), data);
  },
  deletePredefinedProduct: function (reference) {
    return api.delete(uris.predefinedProductURI(reference));
  },
  postPredefinedProductQuotePrice: function (reference, data) {
    return api.post(uris.predefinedProductQuotePriceURI(reference), data);
  },
  postAnalyticsDownloadCustomerBase: function (data) {
    return api.post(uris.analyticsDownloadCustomerBase(), data);
  },
  postAnalyticsDownloadMostPurchasedProducts: function (data) {
    return api.post(uris.analyticsDownloadMostPurchasedProducts(), data);
  },
  postAnalyticsDownloadMostPurchasedFabrics: function (data) {
    return api.post(uris.analyticsDownloadMostPurchasedFabrics(), data);
  },
  postAnalyticsDownloadMostPurchasedProductsFabrics: function (data) {
    return api.post(uris.analyticsDownloadMostPurchasedProductsFabrics(), data);
  },
  postAnalyticsDownloadCustomerBaseOrders: function (data) {
    return api.post(uris.analyticsDownloadCustomerBaseOrders(), data, {
      responseType: 'blob',
    });
  },

  // Web
  patchWebHomeSettings: function (data) {
    return api.patch(uris.webHomeSettingsURI, data);
  },
  getWebHomeSettings: function (data) {
    return api.get(uris.webHomeSettingsURI);
  },

  // Locations
  getLocationsUnpaginated: function () {
    return api.get(uris.locationsUnpaginatedURI);
  },

  getLocationById: function (id) {
    return api.get(uris.locationByIdURI(id));
  },

  /* Stock lines */
  getStockLine: function (id) {
    return api.get(uris.stockURI(id));
  },
  postStockDownloadCSV: function (locationId) {
    return api.post(uris.stockDownloadURI(locationId), null, {
      responseType: 'blob',
    });
  },
  postStockUploadCSV: function (locationId, formData) {
    return api.post(uris.stockUpdateCSVURI(locationId), formData);
  },

  /* Cash */
  getCash: function (locationId) {
    return api.get(uris.cashByLocationURI(locationId));
  },

  postCashRedeemComission: function (data) {
    return api.post(uris.cashRedeemComissionURI, data);
  },

  postCashNewOutgoingPayment: function (data) {
    return api.post(uris.cashNewOutgoingPaymentURI, data);
  },

  // Employees
  getEmployees: function () {
    return api.get(uris.employeesURI);
  },
  getEmployeeById: function (employeeId) {
    return api.get(uris.employeeURI(employeeId));
  },

  // Vouchers
  getActiveVouchers: function (locationId) {
    return api.get(uris.allVouchersByLocationURI(locationId));
  },

  // Workshop tasks
  deleteWorkshopTask: function (workshopTaskID) {
    return api.delete(uris.workshopTaskURI(workshopTaskID));
  },

  getWorkshopTask: function (workshopTaskID) {
    return api.get(uris.workshopTaskURI(workshopTaskID));
  },

  patchWorkshopTask: function (workshopTaskID, data) {
    return api.patch(uris.workshopTaskURI(workshopTaskID), data);
  },

  getWorkshopTasks: function () {
    return api.get(uris.workshopTasksURI);
  },

  getWorkshopTaskFamilies() {
    return api.get(uris.workshopTasksFamiliesURI);
  },

  getWorkshopTasksUnpaginated: function () {
    return api.get(uris.workshopTasksUnpaginatedURI);
  },

  postWorkshopTask: function (data) {
    return api.post(uris.workshopTasksURI, data);
  },

  postWorkshopTasksDownloadExcel: function (family) {
    return api.post(uris.workshopTasksDownloadURI(family), null, {
      responseType: 'blob',
    });
  },
  postWorkshopTasksUploadExcel: function (family, formData) {
    return api.post(uris.workshopTasksUpdateCSVURI(family), formData);
  },

  // Generic Product Attrs
  getGenericProductAttributes: function () {
    return api.get(uris.genericProductAttributesURI);
  },

  getGenericProductAttributesUnpaginated: function () {
    return api.get(uris.genericProductAttributesUnpaginatedURI);
  },
  postGenericProductAttribute: function (data) {
    return api.post(uris.genericProductAttributesURI, data);
  },
  getGenericProductAttribute: function (reference) {
    return api.get(uris.genericProductAttributeURI(reference));
  },
  patchGenericProductAttribute: function (reference, data) {
    return api.patch(uris.genericProductAttributeURI(reference), data);
  },
  deleteGenericProductAttribute: function (reference) {
    return api.delete(uris.genericProductAttributeURI(reference));
  },

  postGenericProductAttributesUploadExcel: function (formData) {
    return api.post(uris.genericProductAttributesUploadExcel(), formData);
  },
  postGenericProductAttributesUploadProductExcel: function (formData) {
    return api.post(
      uris.genericProductAttributesUploadProductExcel(),
      formData
    );
  },

  postGenericProductAttributesDownloadExcel: function () {
    return api.post(uris.genericProductAttributesDownloadExcel(), null, {
      responseType: 'blob',
    });
  },

  postGenericProductAttributesDownloadProductExcel: function () {
    return api.post(uris.genericProductAttributesDownloadProductExcel(), null, {
      responseType: 'blob',
    });
  },

  getGenericProductAttributesHierarchy: function () {
    return api.get(uris.genericProductAttributesHierarchyURI());
  },

  // Product hierarchy

  getProductHierarchy: function (params) {
    const defaults = {
      page_size: '9999',
    };
    params = Object.assign({}, params ? params : {}, defaults);

    return api.get(uris.productHierarchyURI, {
      params: params,
    });
  },
};

const uris = {
  /* Colors */
  colorsURI: 'admin/colors/',
  colorsUnpaginatedURI: 'admin/colors/?page_size=9999',
  colorURI: (id) => {
    return `admin/colors/${id}/`;
  },
  /* Fabrics */
  fabricsURI: 'admin/fabrics/',
  fabricsUnpaginatedURI: 'admin/fabrics/?page_size=9999',
  fabricURI: (id) => {
    return `admin/fabrics/${id}/`;
  },
  /* Interlinings */
  interliningsURI: 'admin/interlinings/',
  interliningsUnpaginatedURI: 'admin/interlinings/?page_size=9999',
  interliningURI: (id) => {
    return `admin/interlinings/${id}/`;
  },
  /* Initials */
  initialsURI: 'admin/initials/',
  initialsUnpaginatedURI: 'admin/initials/?page_size=9999',
  initialURI: (id) => {
    return `admin/initials/${id}/`;
  },
  /* Formulas */
  formulasURI: 'admin/formulas/',
  formulasUnpaginatedURI: 'admin/formulas/?page_size=9999',
  formulaURI: (id) => {
    return `admin/formulas/${id}/`;
  },
  /* Typographies */
  typographiesURI: 'admin/typographies/',
  typographiesUnpaginatedURI: 'admin/typographies/?page_size=9999',
  typographieURI: (id) => {
    return `admin/typographies/${id}/`;
  },
  /* Embroideries */
  embroiderySettingsURI: 'admin/embroiderysettings/',
  embroiderySettingsUnpaginatedURI: 'admin/embroiderysettings/?page_size=9999',
  embroiderySettingURI: (id) => {
    return `admin/embroiderysettings/${id}/`;
  },
  /* Standard Embroideries */
  standardEmbroideriesURI: 'admin/standardembroideries/',
  standardEmbroideriesUnpaginatedURI:
    'admin/standardembroideries/?page_size=9999',
  standardEmbroideryURI: (id) => {
    return `admin/standardembroideries/${id}/`;
  },
  /* Customized Embroideries */
  customizedEmbroideriesURI: 'admin/customizedembroideries/',
  customizedEmbroideriesUnpaginatedURI:
    'admin/customizedembroideries/?page_size=9999',
  customizedEmbroideryURI: (id) => {
    return `admin/customizedembroideries/${id}/`;
  },
  /* Stitches */
  stitchesURI: 'admin/stitches/',
  stitchesUnpaginatedURI: 'admin/stitches/?page_size=9999',
  stitchURI: (reference) => {
    return `admin/stitches/${reference}/`;
  },
  /* Product attributes */
  productAttributesURI: 'admin/productattributes/',
  productAttributesUnpaginatedURI: 'admin/productattributes/?page_size=9999',
  productAttributeURI: (reference) => {
    return `admin/productattributes/${reference}/`;
  },
  /* Product selectors */
  productSelectorsURI: 'admin/productselectors/',
  productSelectorsByAttributeURI: (attribute_id) => {
    return `admin/productselectors/?attribute_id=${attribute_id}`;
  },
  productSelectorsByAttributeUnpaginatedURI: (attribute_id) => {
    return `admin/productselectors/?attribute_id=${attribute_id}&page_size=9999`;
  },
  productSelectorsUnpaginatedURI: 'admin/productselectors/?page_size=9999',
  productSelectorURI: (reference) => {
    return `admin/productselectors/${reference}/`;
  },

  /* Generic product attributes */
  genericProductAttributesURI: 'admin/genericproductattributes/',

  genericProductAttributesUnpaginatedURI:
    'admin/genericproductattributes/?page_size=9999',
  genericProductAttributeURI: (reference) => {
    return `admin/genericproductattributes/${reference}/`;
  },
  genericProductAttributesUploadExcel: () => {
    return `admin/genericproductattributes/upload_excel/`;
  },
  genericProductAttributesUploadProductExcel: () => {
    return `admin/genericproductattributes/upload_product_excel/`;
  },
  genericProductAttributesDownloadExcel: () => {
    return `admin/genericproductattributes/download_excel/`;
  },
  genericProductAttributesDownloadProductExcel: () => {
    return `admin/genericproductattributes/download_product_excel/`;
  },
  genericProductAttributesHierarchyURI: () => {
    return `admin/genericproductattributes/hierarchy/`;
  },

  // Product hierarchy
  productHierarchyURI: 'admin/attribute-hierarchy/',

  /* Product templates */
  productTemplatesURI: 'admin/producttemplates/',
  productTemplatesUnpaginatedURI: 'admin/producttemplates/?page_size=9999',
  productTemplateURI: (reference) => {
    return `admin/producttemplates/${reference}/`;
  },
  /* Products */
  productsURI: 'admin/products/',
  productsUnpaginatedURI: 'admin/products/?page_size=9999&is_discontinued=0',
  productsWithStockURI: (locationId) => {
    return `admin/products/with_stock/?page_size=10&location=${locationId}`;
  },
  productsGlobalSearchURI: (txt) => {
    return `admin/products/?search=${txt}&page_size=9999&is_discontinued=0`;
  },
  productsWithStockGlobalSearchURI: (location, txt, txtVariant) => {
    return `admin/products/with_stock/?search=${txt}&search_variant=${txtVariant}&page_size=10&is_discontinued=0&location=${location}`;
  },
  productURI: (reference) => {
    return `admin/products/${reference}/`;
  },
  productQuotePriceURI: (reference) => {
    return `admin/products/${reference}/quote_price/`;
  },
  productDuplicateURI: (id) => {
    return `admin/products/${id}/duplicate/`;
  },
  /* Product categories */
  productCategoriesURI: 'admin/productcategories/',
  productCategoriesUnpaginatedURI: 'admin/productcategories/?page_size=9999',
  productCategoryURI: (reference) => {
    return `admin/productcategories/${reference}/`;
  },
  /* Product categories */
  embroideryCategoriesURI: 'admin/embroiderycategories/',
  embroideryCategoriesUnpaginatedURI:
    'admin/embroiderycategories/?page_size=9999',
  embroideryCategoryURI: (reference) => {
    return `admin/embroiderycategories/${reference}/`;
  },
  /* Predefined product categories */
  predefinedProductCategoriesURI: 'admin/predefinedproductcategories/',
  predefinedProductCategoriesUnpaginatedURI:
    'admin/predefinedproductcategories/?page_size=9999',
  predefinedProductCategoryURI: (reference) => {
    return `admin/predefinedproductcategories/${reference}/`;
  },
  /* Customers */
  customersURI: 'admin/customers/',
  customersGlobalSearchURI: (txt) => {
    return `admin/customers/?search=${txt}`;
  },
  customersUnpaginatedURI: 'admin/customers/?page_size=9999',
  customerURI: (reference) => {
    return `admin/customers/${reference}/`;
  },

  /* billingaddresses */
  billingAddressesURI: 'admin/billingaddresses/',
  billingAddressesUnpaginatedURI: 'admin/billingaddresses/?page_size=9999',
  billingAddressesByCustomerURI: (customerID, excludeHidden) => {
    if (excludeHidden === 1) {
      return `admin/billingaddresses/?customer_id=${customerID}&hidden=0&page_size=9999`;
    }
    return `admin/billingaddresses/?customer_id=${customerID}&page_size=9999`;
  },
  billingAddressURI: (id) => {
    return `admin/billingaddresses/${id}/`;
  },

  /* shippingaddresses */
  shippingAddressesURI: 'admin/shippingaddresses/',
  shippingAddressesUnpaginatedURI: 'admin/shippingaddresses/?page_size=9999',
  shippingAddressesByCustomerURI: (customerID, excludeHidden) => {
    if (excludeHidden === 1) {
      return `admin/shippingaddresses/?customer_id=${customerID}&hidden=0&page_size=9999`;
    }
    return `admin/shippingaddresses/?customer_id=${customerID}&page_size=9999`;
  },
  shippingAddressURI: (reference) => {
    return `admin/shippingaddresses/${reference}/`;
  },

  /* orders */
  ordersURI: 'admin/orders/',
  ordersUnpaginatedURI: 'admin/orders/?page_size=9999',
  orderURI: (reference) => {
    return `admin/orders/${reference}/`;
  },
  ordersByLocationURI: (locationID) => {
    return `admin/orders/?location=${locationID}`;
  },

  /* workshop orders */
  workshopOrdersURI: 'admin/workshoporders/',
  workshopNoStockOrdersURI: 'admin/workshoporders/?without_quotes_and_web=0',
  workshopStockOrdersURI: 'admin/workshoporders/?without_quotes_and_web=1',
  workshopOrdersUnpaginatedURI: 'admin/workshoporders/?page_size=9999',
  workshopOrderURI: (reference) => {
    return `admin/workshoporders/${reference}/`;
  },
  workshopOrderGetStockURI: 'admin/workshoporders/get_stock_and_price/',
  workshopOrderWorkshopDownloadURI: (id) => {
    return `admin/workshoporders/${id}/download_workshop/`;
  },
  workshopOrderLegalDownloadURI: (id) => {
    return `admin/workshoporders/${id}/download_legal/`;
  },
  workshopOrderLegalDownloadPartialURI: (id) => {
    return `admin/workshoporders/${id}/generate_invoice_partial/`;
  },
  workshopOrderLegalDownloadTotalURI: (id) => {
    return `admin/workshoporders/${id}/generate_invoice_total/`;
  },
  workshopOrderLegalPreviewTotalURI: (id) => {
    return `admin/workshoporders/${id}/preview_invoice_total/`;
  },
  workshopOrderLegalPreviewPartialURI: (id) => {
    return `admin/workshoporders/${id}/preview_invoice_partial/`;
  },
  workshopOrderSyncFromQuoteURI: (id) => {
    return `admin/workshoporders/${id}/sync_from_quote/`;
  },

  /* deliveries */
  deliveriesURI: 'admin/deliveries/',
  deliveriesUnpaginatedURI: 'admin/deliveries/?page_size=9999',
  deliveryURI: (reference) => {
    return `admin/deliveries/${reference}/`;
  },
  deliveryGetStockURI: 'admin/deliveries/get_stock_and_price/',
  deliveryDownloadURI: (id) => {
    return `admin/deliveries/${id}/download/`;
  },
  deliveriesQualifiedWorkshopOrdersURI:
    'admin/deliveries/qualified_workshop_orders/',
  deliveriesWorkshopOrdersURI: (deliveryId) =>
    `admin/deliveries/qualified_workshop_orders/?delivery_id=${deliveryId}`,

  /* invoices */
  invoicesURI: 'admin/invoices/',
  invoicesByCustomerIdURI: (customerId) => {
    return `admin/invoices/?customer_id=${customerId}`;
  },
  invoicesUnpaginatedURI: 'admin/invoices/?page_size=9999',
  invoiceURI: (id) => {
    return `admin/invoices/${id}/`;
  },

  /* orders status history */
  orderStatusHistoriesURI: 'admin/orders_status_history/',
  orderStatusHistoriesByOrderURI: (orderID) => {
    return `admin/orders_status_history/?order_id=${orderID}`;
  },

  /* workshop orders status history */
  workshopOrderStatusHistoriesURI: 'admin/workshop_orders_status_history/',
  workshopOrderStatusHistoriesByWorkshopOrderURI: (orderID) => {
    return `admin/workshop_orders_status_history/?order_id=${orderID}`;
  },

  // Deliveries status history
  deliveryStatusHistoriesURI: 'admin/deliveries_status_history/',
  deliveryStatusHistoriesByDeliveryURI: (deliveryID) => {
    return `admin/deliveries_status_history/?delivery_id=${deliveryID}`;
  },

  // Orders

  ordersUncompletedURI: 'admin/orders/uncompleted_orders/',
  orderPriceURI: 'admin/orders/order_price/',

  ordersUnpaginatedURI: 'admin/orders/?page_size=9999',
  orderURI: (reference) => {
    return `admin/orders/${reference}/`;
  },
  orderDownloadURI: (id) => {
    return `admin/orders/${id}/download/`;
  },
  orderLegalDownloadURI: (id) => {
    return `admin/orders/${id}/download_legal/`;
  },
  invoiceDownloadURI: (id) => {
    return `admin/invoices/${id}/download/`;
  },
  orderLegalDownloadPartialURI: (id) => {
    return `admin/orders/${id}/generate_invoice_partial/`;
  },
  orderLegalDownloadTotalURI: (id) => {
    return `admin/orders/${id}/generate_invoice_total/`;
  },
  orderWorkshopDownloadURI: (id) => {
    return `admin/orders/${id}/download_workshop/`;
  },

  /* quotes */
  quotesURI: 'admin/quotes/',
  quotesByLocationURI: (locationID) => {
    return `admin/quotes/?location=${locationID}`;
  },
  quotesUnpaginatedURI: 'admin/quotes/?page_size=9999',
  quotesGlobalSearchURI: (txt) => {
    return `admin/quotes/?search=${txt}`;
  },
  quoteURI: (reference) => {
    return `admin/quotes/${reference}/`;
  },
  quoteDownloadURI: (id) => {
    return `admin/quotes/${id}/download/`;
  },
  quoteMarkPaidURI: (id) => {
    return `admin/quotes/${id}/mark_paid/`;
  },
  quotePriceURI: 'admin/quotes/quote_price/',
  /* countries */
  countriesURI: 'admin/countries/',
  countriesUnpaginatedURI: 'admin/countries/?page_size=9999',
  countryURI: (reference) => {
    return `admin/countrie/${reference}/`;
  },
  quoteWorkshopDownloadURI: (id) => {
    return `admin/quotes/${id}/download_workshop/`;
  },
  quoteDuplicateURI: (id) => {
    return `admin/quotes/${id}/duplicate/`;
  },

  /* shippingmethods */
  shippingMethodsURI: 'admin/shippingmethods/',
  shippingMethodsUnpaginatedURI: 'admin/shippingmethods/?page_size=9999',
  shippingMethodURI: (reference) => {
    return `admin/shippingmethods/${reference}`;
  },

  /* fabriccompositions */
  fabricCompositionsUnpaginatedURI: 'admin/fabriccompositions/?page_size=9999',

  /* fabriccolorfamilies */
  fabricColorFamiliesUnpaginatedURI:
    'admin/fabriccolorfamilies/?page_size=9999',

  /* fabricdesigns */
  fabricDesignsUnpaginatedURI: 'admin/fabricdesigns/?page_size=9999',

  /* Embroidered Strips */
  embroideredStripsURI: 'admin/embroideredstrips/',
  embroideredStripsUnpaginatedURI: 'admin/embroideredstrips/?page_size=9999',
  embroideredStripURI: (id) => {
    return `admin/embroideredstrips/${id}/`;
  },

  /* Predefined products */
  predefinedProductsURI: 'admin/predefinedproducts/',
  predefinedProductsUnpaginatedURI:
    'admin/predefinedproducts/?page_size=9999&is_discontinued=0',
  predefinedProductsWithStockURI: (locationId) => {
    return `admin/predefinedproducts/with_stock/?page_size=5&location=${locationId}`;
  },
  predefinedProductsGlobalSearchURI: (txt) => {
    return `admin/predefinedproducts/?search=${txt}&page_size=9999&is_discontinued=0`;
  },
  predefinedProductsWithStockGlobalSearchURI: (location, txt) => {
    return `admin/predefinedproducts/with_stock/?search=${txt}&page_size=10&is_discontinued=0&location=${location}`;
  },
  predefinedProductURI: (reference) => {
    return `admin/predefinedproducts/${reference}/`;
  },
  predefinedProductQuotePriceURI: (reference) => {
    return `admin/predefinedproducts/${reference}/quote_price/`;
  },
  analyticsDownloadCustomerBase: () => {
    return 'admin/analytics/download_customerbase/';
  },
  analyticsDownloadMostPurchasedProducts: () => {
    return 'admin/analytics/download_most_purchased_products/';
  },
  analyticsDownloadMostPurchasedFabrics: () => {
    return 'admin/analytics/download_most_purchased_fabrics/';
  },
  analyticsDownloadMostPurchasedProductsFabrics: () => {
    return 'admin/analytics/download_most_purchased_products_fabrics/';
  },
  analyticsDownloadCustomerBaseOrders: () => {
    return 'admin/analytics/download_customerbase_orders/';
  },

  // Locations
  locationsUnpaginatedURI: 'admin/locations/?page_size=9999',
  locationByIdURI: (id) => {
    return `admin/locations/${id}`;
  },

  // Web
  webHomeSettingsURI: 'admin/webhomesettings/0/',

  /* Stock lines */
  stockURI: 'admin/stock_lines/',
  stockUnpaginatedURI: 'admin/stock_lines/?page_size=9999',
  stockURI: (reference) => {
    return `admin/stock_lines/${reference}/`;
  },
  stockChangesURI: (reference) => {
    return `admin/stock_lines/${reference}/changes`;
  },
  stockByLocationURI: (locationID) => {
    return `admin/stock_lines/?location=${locationID}`;
  },
  stockDownloadURI: (locationID) => {
    return `admin/stock_lines/download_csv/?location=${locationID}`;
  },
  stockUpdateCSVURI: (locationID) => {
    return `admin/stock_lines/upload_csv/?location=${locationID}`;
  },

  /* Cash */
  cashURI: 'admin/cash/',
  cashUnpaginatedURI: 'admin/cash/?page_size=9999',
  cashURI: (reference) => {
    return `admin/cash/${reference}/`;
  },
  cashChangesURI: (reference) => {
    return `admin/cash/${reference}/changes`;
  },
  cashByLocationURI: (locationID) => {
    return `admin/cash/?location=${locationID}`;
  },
  cashPendingSalesComissionByLocationURI: (locationID) => {
    return `admin/cash/comission_sales/?location=${locationID}`;
  },
  cashRedeemComissionURI: 'admin/cash/redeem_comission/',
  cashNewOutgoingPaymentURI: 'admin/cash/register_outgoing_payment/',

  // Employees

  employeesURI: 'admin/employees/',
  employeeURI: (employeeId) => {
    return `admin/employees/${employeeId}/`;
  },

  // Commissions
  commissionsGeneratedByEmployeeAndLocation: (locationID, employeeId) => {
    return `admin/commisions/?location=${locationID}&comission_maker=${employeeId}`;
  },
  commissionPaymentsByEmployeeAndLocation: (locationID, employeeId) => {
    return `admin/comission-payments/?location=${locationID}&comission_maker=${employeeId}`;
  },

  // Vouchers
  vouchersURI: 'admin/vouchers/',
  vouchersByLocationURI: (locationID) => {
    return `admin/vouchers/?location=${locationID}`;
  },
  allVouchersByLocationURI: (locationID) => {
    return `admin/vouchers/?location=${locationID}&page_size=9999`;
  },
  vouchersByLocationUnpaginatedURI: (locationID) => {
    return `admin/vouchers/?location=${locationID}&page_size=9999`;
  },

  // Workshop tasks
  workshopTasksUnpaginatedURI: 'admin/workshop-tasks/?page_size=9999',
  workshopTasksURI: 'admin/workshop-tasks/',
  workshopTasksPerFamilyURI: (family) => {
    return `admin/workshop-tasks/?attr_family=${family}&page_size=9999`;
  },
  workshopTasksFamiliesURI: 'admin/workshop-tasks/families',
  workshopTaskURI: (workshopTaskID) => {
    return `admin/workshop-tasks/${workshopTaskID}/`;
  },
  workshopTasksDownloadURI: (family) => {
    return `admin/workshop-tasks/download_excel/?attr_family=${family}`;
  },
  workshopTasksUpdateCSVURI: (family) => {
    return `admin/workshop-tasks/upload_excel/?attr_family=${family}`;
  },
};

export { actions, uris, api };
export default api;
